import React, { useState } from "react";
import ReactDOM from "react-dom";
import FooterPagination from "../../Pagination/FooterPagination";
import {
  Button,
  DropdownToggle,
  UncontrolledDropdown,
  DropdownItem,
  DropdownMenu,
  Table,
  ButtonGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import {
  getCEbyUserId,
  deleteAppUser,
} from "../../../slices/admin/thunk";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import UserCEModal from "../../modals/UsersAppModal/UserCEModal";
import UserStatusBadge from "../../Badges/UserStatusBadge";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../../../slices/auth/hooks";
import { DASHBOARD_USER_ROLES } from "../../../constants";

const UsersAppTable = ({
  data,
  searchDone,
  searchParam,
  onRefresh,
  paginationProps,
  formatDate,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.role === DASHBOARD_USER_ROLES.ADMIN;

  const isNotSearchResult = searchParam && !data.length && searchDone;
  const noData = data.length === 0 && !searchParam;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userData, setUserData] = useState({});
  const [loadingUserData, setLoadingUserData] = useState(false);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // Helper function to handle upline click
  const handleUplineClick = (uplineType, uplineId) => {
    const routes = {
      nationalId: `/nationals/${uplineId}`,
      gaId: `/ga/${uplineId}`,
      brokerId: `/brokers/${uplineId}`,
    };

    const url = routes[uplineType];
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleViewMember = async (userId) => {
    toggleModal();
    // navigate to /admin/users/${userId}

    const navigateUrl = `/admin/member-management/${userId}`;

    navigate(navigateUrl)
  }

  const handleViewClientEligibility = async (userId) => {
    toggleModal();
    try {
      setLoadingUserData(true);
      const response = await dispatch(getCEbyUserId({ userId }));
      console.log(response);
      if (response.payload) {
        setUserData(response.payload);
      } else {
        setUserData(null);
      }
      setLoadingUserData(false);
    } catch (error) {
      console.error("fetchDataByUserId -> error", error);
      setLoadingUserData(false);
      setUserData(null);
    }
  };

  // Delete user handlers
  const handleOpenDeleteModal = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setUserToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      const response = await dispatch(deleteAppUser({ userId: userToDelete.id }));
      if (response.payload?.success) {
        toast.success(`User ${userToDelete.email || userToDelete.name} deleted successfully`);
        handleCloseDeleteModal();
        if (onRefresh) {
          onRefresh();
        }
      } else {
        toast.error(response.error?.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("handleConfirmDelete -> error", error);
      toast.error(error.message || "Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper function to get user type display text
  const getUserTypeDisplay = (type) => {
    switch (type) {
      case 'individual':
        return 'Individual';
      case 'employee':
        return 'Employee';
      case 'user':
        return 'User';
      default:
        return 'Individual';
    }
  };

  // Helper function to get user type badge color
  const getUserTypeBadgeColor = (type) => {
    switch (type) {
      case 'individual':
        return 'primary';
      case 'employee':
        return 'info';
      case 'user':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const renderUplineBadges = (upline) => {
    if (!upline || typeof upline !== 'object') {
      return <span className="text-muted">-</span>;
    }

    const badges = [];

    // Check for National
    if (upline.nationalId) {
      badges.push(
        <UserStatusBadge
          key="national"
          status="National"
          color="success"
          className="me-1 cursor-pointer"
          onClick={() => handleUplineClick('nationalId', upline.nationalId)}
          style={{ cursor: 'pointer' }}
        />
      );
    }

    // Check for GA
    if (upline.gaId) {
      badges.push(
        <UserStatusBadge
          key="ga"
          status="GA"
          color="warning"
          className="me-1 cursor-pointer"
          onClick={() => handleUplineClick('gaId', upline.gaId)}
          style={{ cursor: 'pointer' }}
        />
      );
    }

    // Check for Broker
    if (upline.brokerId) {
      badges.push(
        <UserStatusBadge
          key="broker"
          status="Broker"
          color="info"
          className="me-1 cursor-pointer"
          onClick={() => handleUplineClick('brokerId', upline.brokerId)}
          style={{ cursor: 'pointer' }}
        />
      );
    }

    return badges.length > 0 ? (
      <div className="d-flex flex-wrap gap-1">
        {badges}
      </div>
    ) : (
      <span className="text-muted">-</span>
    );
  };

  const renderDropdownMenu = (user) => {
    const portalRoot = document.getElementById("portal-root");
    return portalRoot
      ? ReactDOM.createPortal(
        <DropdownMenu>
          <DropdownItem
            onClick={() => handleViewMember(user.id)}
            className="edit-item-btn"
          >
            <i className="ri-eye-fill align-bottom me-2 text-muted"></i>
            View Profile
          </DropdownItem>
          <DropdownItem
            onClick={() => handleViewClientEligibility(user.id)}
            className="edit-item-btn"
          >
            <i className="ri-eye-fill align-bottom me-2 text-muted"></i>
            View Client Eligibility
          </DropdownItem>
          {isAdmin && (
            <>
              <DropdownItem divider />
              <DropdownItem
                onClick={() => handleOpenDeleteModal(user)}
                className="text-danger"
              >
                <i className="ri-delete-bin-line align-bottom me-2"></i>
                Delete User
              </DropdownItem>
            </>
          )}
        </DropdownMenu>,
        portalRoot
      )
      : null;
  };

  return (
    <div>
      {isModalOpen && (
        <UserCEModal
          open={isModalOpen}
          setOpen={setIsModalOpen}
          userData={userData}
          loading={loadingUserData}
        />
      )}
      <Table className="table table-hover border table-centered align-middle table-nowrap mb-0">
        <thead className="bg" style={{ background: "var(--thead-bg)" }}>
          <tr>
            <th>Member Id</th>
            <th>Name</th>
            <th>Email</th>
            <th>BaxRx+</th>
            <th>Type</th>
            <th>Upline</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {isNotSearchResult && (
            <tr>
              <td className="text-center" colSpan="7">
                No search results for "{searchParam}"
              </td>
            </tr>
          )}
          {noData && (
            <tr>
              <td className="text-center" colSpan="7">
                No data available
              </td>
            </tr>
          )}
          {data?.map((user) => (
            <tr
              onClick={() => handleViewMember(user.id)}
              className="cursor-pointer" key={user.id || user.memberId}>
              <td>
                <span className="fw-medium">{user.memberId || 'N/A'}</span>
              </td>
              <td>{user.name || 'N/A'}</td>
              <td>{user.email || 'N/A'}</td>
              <td className="text-start ps-4">
                <UserStatusBadge
                  status={user.baxrxPlus === 'active' ? 'Active' : 'Inactive'}
                  color={user.baxrxPlus === 'active' ? 'success' : 'danger'}
                />
              </td>
              <td className="text-start ps-4">
                <div className="d-flex align-items-center gap-1">
                  <UserStatusBadge
                    status={getUserTypeDisplay(user.type)}
                    color={getUserTypeBadgeColor(user.type)}
                  />
                  {user.isFamilyPlan && (
                    <span className="badge bg-info text-white" style={{ fontSize: '0.7rem', padding: '0.25rem 0.4rem' }}>
                      F
                    </span>
                  )}
                  {(user.isDependent || user.dependentContext?.isDependent) && (
                    <span className="badge bg-secondary text-white" style={{ fontSize: '0.7rem', padding: '0.25rem 0.4rem' }}>
                      D
                    </span>
                  )}
                </div>
              </td>
              <td className="text-center">
                {/* {renderUplineBadges(user.upline)} */}
                {/* // Render yes/no based on user.upline boolean. */}
                {user.upline ? <span className="text-success">Yes</span> : <span className="text-danger">No</span>}
              </td>
              <td className="text-center">
                <ButtonGroup onClick={(e) => e.stopPropagation()}>
                  <UncontrolledDropdown className="dropdown d-inline-block">
                    <DropdownToggle
                      tag="button"
                      className="btn btn-soft-primary btn-sm"
                    >
                      <i className="ri-more-fill align-middle"></i>
                    </DropdownToggle>
                    {renderDropdownMenu(user)}
                  </UncontrolledDropdown>
                </ButtonGroup>
              </td>
            </tr>
          ))}
        </tbody>
        {paginationProps.totalItems > 0 && (
          <tfoot>
            <FooterPagination {...paginationProps} />
          </tfoot>
        )}
      </Table>
      <div id="portal-root"></div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={handleCloseDeleteModal} centered>
        <ModalHeader toggle={handleCloseDeleteModal} className="bg-danger-subtle">
          <span className="text-danger">Delete User</span>
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <i className="ri-error-warning-line text-danger" style={{ fontSize: '4rem' }}></i>
            <h5 className="mt-3">Are you sure you want to delete this user?</h5>
            {userToDelete && (
              <div className="mt-3 p-3 bg-light rounded">
                <p className="mb-1"><strong>Name:</strong> {userToDelete.name || 'N/A'}</p>
                <p className="mb-1"><strong>Email:</strong> {userToDelete.email || 'N/A'}</p>
                <p className="mb-0"><strong>Member ID:</strong> {userToDelete.memberId || 'N/A'}</p>
              </div>
            )}
            <p className="text-danger mt-3 mb-0">
              <strong>Warning:</strong> This action cannot be undone. The user will be permanently deleted.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleCloseDeleteModal} disabled={isDeleting}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="ri-delete-bin-line me-1"></i>
                Delete User
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default UsersAppTable;